"use client";

import type { ReactNode } from "react";
import { Switch } from "../switch";
import { buildControlDescribedBy, fieldMixedId } from "./field-a11y";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface BooleanGroupOption {
	key: string;
	label: string;
	description?: string;
}

export interface BooleanGroupFieldProps {
	id: string;
	label: string;
	value: Record<string, boolean>;
	options: BooleanGroupOption[];
	onChange: (value: Record<string, boolean>) => void;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	layoutHint?: FieldLayoutHint;
	className?: string;
	disabled?: boolean;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
}

export function BooleanGroupField({
	id,
	label,
	value,
	options,
	onChange,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	layoutHint,
	className,
	disabled = false,
	mixed = false,
	mixedTooltip,
	mixedPlaceholder = "Mixed values",
}: BooleanGroupFieldProps) {
	const mixedId = fieldMixedId(id);
	const controlDescribedBy = buildControlDescribedBy({
		id,
		error,
		hasDescription: Boolean(description),
		mixedId: mixed ? mixedId : undefined,
	});

	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={className}
			describedById={mixed ? mixedId : undefined}
		>
			{name ? (
				<input
					type="hidden"
					name={name}
					value={JSON.stringify(value)}
					readOnly
				/>
			) : null}
			{mixed && mixedTooltip ? (
				<div className="mb-2 text-xs text-muted-foreground">{mixedTooltip}</div>
			) : null}
			{mixed ? (
				<p
					id={mixedId}
					data-testid={`${id}-mixed-placeholder`}
					className="mb-2 text-xs text-muted-foreground"
				>
					{mixedPlaceholder}
				</p>
			) : null}
			<ul
				className="flex flex-col gap-3"
				aria-label={label}
				onBlur={onBlur}
			>
				{options.map((option) => {
					const switchId = `${id}-${option.key}`;
					const checked = !mixed && value[option.key] === true;
					return (
						<li
							key={option.key}
							className="flex items-start justify-between gap-3"
						>
							<div className="min-w-0">
								<label
									htmlFor={switchId}
									className="text-sm font-medium text-foreground"
								>
									{option.label}
								</label>
								{option.description ? (
									<p className="text-xs text-muted-foreground">
										{option.description}
									</p>
								) : null}
							</div>
							<Switch
								id={switchId}
								checked={checked}
								disabled={disabled || mixed}
								onCheckedChange={(next) =>
									onChange({ ...value, [option.key]: next })
								}
								aria-label={option.label}
								aria-invalid={Boolean(error) ? true : undefined}
								aria-describedby={controlDescribedBy}
							/>
						</li>
					);
				})}
			</ul>
		</FieldShell>
	);
}
